/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prefer-const */
import React, { useCallback, useEffect, useRef } from 'react';
import { isDesktop, isChrome } from 'react-device-detect';
import { showInitMessage } from './helpers';
import {
  IGamecastStreamSession,
  IGameCast,
  IGameCastArgs,
  IGamecastServerDisconnectReason,
  IGamecastVideoStats,
} from './interfaces';
import GameCastService from '@services/GameCastService';
import {
  GAME_CAST_REQUEST_TIMEOUT,
  enableDebug,
  disallowKeyCode,
} from './constants';
import { IInitMessage } from '../Pureweb/interfaces';
import { IParticipant } from '@interfaces/participants';
import { IRoom } from '@interfaces/rooms';
import { StreamingProviders } from '@core/dashboard/providers/MytaverseEventProvider/interfaces';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useChatState } from '@core/chat';
import { RegisterTouchToMouse } from './touchtomouse';
import { IStartStreamSessionOutput } from '@interfaces/gamecast';
import { reportUEGameliftCrash } from '@helpers/sentry';

import { default as gameliftStreamsLogger } from './logger';

import { env } from 'env';
import { formatString } from '@helpers/string';

const { REACT_APP_GAMELIFT_STREAMS_CONNECTION_TIMEOUT_SECONDS = 5 * 60 } = env;
const WEBRTC_STATS_MIN_SAMPLES = 450;

export const useGameInitialization = ({
  streamId,
  setIsGamecastCrash,
  setIsGamecastTimeout,
  gamecastStats,
  currentRoom,
  externalKeyboardConnected,
  setShowExternalKeyboardOverlay,
}: {
  browserType: string;
  streamId: string | null;
  currentParticipant: IParticipant | undefined;
  setStreamingProvider: React.Dispatch<
    React.SetStateAction<StreamingProviders | null>
  >;
  setIsGamecastCrash: React.Dispatch<React.SetStateAction<boolean>>;
  setIsGamecastTimeout: React.Dispatch<React.SetStateAction<boolean>>;
  gamecastStats: React.MutableRefObject<IGamecastVideoStats | null>;
  currentRoom: IRoom | null | undefined;
  externalKeyboardConnected: boolean;
  setShowExternalKeyboardOverlay: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { sendMessageToUnreal, navigateToEventsPage, user } = useMytaverse();
  const { setOpen: setOpenChat } = useChatState();

  const gamecastClientRef = useRef<null | IGameCast>(null);
  const gamecastClientConnectedRef = useRef(false);
  const gamecastRTCStatsIntervalRef = useRef<any>();
  const streamStartedConnectionRef = useRef<boolean>(false);
  const abortConnectionRef = useRef<boolean>(false);
  const gamecastConnectionTimeoutRef = React.useRef<NodeJS.Timeout | null>(
    null,
  );

  // Store session context for crash reporting
  const sessionContextRef = useRef<{
    sessionArn?: string;
    region?: string;
    userId?: string;
  }>({});

  //@ts-ignore
  const GamecastClient = window.gameliftstreams.GameLiftStreams;
  window.gameliftstreams.setLogLevel('debug');

  let connectedTime = 0;

  if (enableDebug) {
    //@ts-ignore
    window.gameliftstreams.setLogLevel('debug');
  }

  const closeGamecastStream = () => {
    abortConnectionRef.current = true;

    if (gamecastClientRef.current) {
      gameliftStreamsLogger.log(`Closing gamelift stream session`);
      gamecastClientRef.current.close();
      gamecastClientRef.current = null;
    }

    if (gamecastRTCStatsIntervalRef.current) {
      clearInterval(gamecastRTCStatsIntervalRef.current);
      gamecastRTCStatsIntervalRef.current = null;
    }
  };

  const abortGamecastConnection = () => {
    if (!streamStartedConnectionRef.current) {
      gameliftStreamsLogger.log(`Aborting gamelift streams connection`);
    }
    abortConnectionRef.current = true;

    closeGamecastStream();
  };

  const handleGamecastConnectionTimeout = useCallback(() => {
    gameliftStreamsLogger.log('Connection timeout');

    setIsGamecastTimeout(true);
  }, [setIsGamecastTimeout]);

  const clearGamecastConnectionTimeout = useCallback(() => {
    if (gamecastConnectionTimeoutRef.current !== null) {
      gameliftStreamsLogger.log('Clear connection timeout');
      clearTimeout(gamecastConnectionTimeoutRef.current);
      gamecastConnectionTimeoutRef.current = null;
    }
  }, []);

  const initializeGamecastConnectionTimeout = useCallback(() => {
    clearGamecastConnectionTimeout();

    gameliftStreamsLogger.log('Start connection timeout');

    gamecastConnectionTimeoutRef.current = setTimeout(
      handleGamecastConnectionTimeout,
      Number(REACT_APP_GAMELIFT_STREAMS_CONNECTION_TIMEOUT_SECONDS) * 1000,
    );
  }, [handleGamecastConnectionTimeout, clearGamecastConnectionTimeout]);

  const getGameCastClient = (
    videoElement: HTMLVideoElement,
    isReconnect?: boolean,
  ) => {
    const audioElement = document.getElementById(
      'gamecastAudioTag',
    )! as HTMLAudioElement;

    const gamecastClient: IGameCast = new GamecastClient({
      videoElement,
      audioElement,
      inputConfiguration: {
        autoMouse: true,
        autoKeyboard: true,
        autoGamepad: true,
        resetOnDetach: true,
        hapticFeedback: true,
        setCursor: 'visibility',
        autoPointerLock: 'fullscreen',
        keyboardFilter: (event) => {
          if (enableDebug) {
            console.warn('gamecast keyboard event', { event });
          }

          if (
            user?.roles?.length &&
            user.roles.indexOf('SUPER_ADMIN') === -1 &&
            disallowKeyCode.indexOf(event.code) > -1
          ) {
            if (enableDebug) {
              console.warn('disallow key input', event.code);
            }
            return false;
          }

          if (event.metaKey) {
            if (enableDebug) {
              console.warn('disallow key input', event.code);
            }
            return false;
          }

          if (event.key === 'F11') {
            if (enableDebug) {
              console.warn('disallow key input', event.code);
            }
            return false;
          }

          return true;
        },
      },
      clientConnection: {
        connectionState: (state) => {
          gameliftStreamsLogger.log(`Connection state: ${state}`);

          if (state === 'connected') {
            connectedTime = Date.now();
          }
        },
        channelError: (state) => {
          gameliftStreamsLogger.log(`Channel error: ${state}`);

          if (state) {
            reportUEGameliftCrash({
              sessionArn: sessionContextRef.current.sessionArn,
              region: sessionContextRef.current.region,
              disconnectReason: `channel_error: ${state}`,
              userId: sessionContextRef.current.userId,
              streamId: streamId || undefined,
            });
          }
        },
        serverDisconnect: async (state: IGamecastServerDisconnectReason) => {
          switch (state) {
            case IGamecastServerDisconnectReason.Terminated:
              gameliftStreamsLogger.log(`Gamecast server terminated: ${state}`);

              reportUEGameliftCrash({
                sessionArn: sessionContextRef.current.sessionArn,
                region: sessionContextRef.current.region,
                disconnectReason: state,
                userId: sessionContextRef.current.userId,
                streamId: streamId || undefined,
              });

              if (gamecastClientRef.current) {
                gamecastClientRef.current.close();
              }
              setIsGamecastCrash(true);

              if (isReconnect) {
                //setOpenChat(false);
              }
              break;
            default:
              gameliftStreamsLogger.log(`Server disconnect: ${state}`);
              break;
          }
        },
        applicationMessage: (state) => {
          try {
            const message: any = JSON.parse(new TextDecoder().decode(state));

            gameliftStreamsLogger.log(`Application message: ${message}`);

            if (message.action === 'CLOSE_TAB') {
              window.close();
            }
          } catch (e) {
            console.error(`Error handling application message: ${e}`);
          }
        },
      },
    } as IGameCastArgs);

    return gamecastClient;
  };

  const processActiveStreamSession = async (
    gamecastStreamSession: IStartStreamSessionOutput,
  ) => {
    let isStarted = false;

    if (gamecastClientConnectedRef.current) {
      gameliftStreamsLogger.log(
        `Skip processSignalResponse for sessionId: ${gamecastStreamSession.Arn?.split(
          '/',
        ).pop()}. Gamelift steams session already connected`,
      );
      isStarted = true;
    } else if (
      gamecastStreamSession &&
      !abortConnectionRef.current &&
      gamecastClientRef.current
    ) {
      clearGamecastConnectionTimeout();
      gameliftStreamsLogger.log(
        `Start processSignalResponse for sessionId: ${gamecastStreamSession.Arn?.split(
          '/',
        ).pop()}`,
      );

      gamecastClientConnectedRef.current = true;
      await gamecastClientRef.current.processSignalResponse(
        gamecastStreamSession?.SignalResponse as string,
      );
      gamecastClientRef.current.attachInput();
      isStarted = true;
    }

    if (abortConnectionRef.current) {
      abortGamecastConnection();
      isStarted = false;
    }

    streamStartedConnectionRef.current = isStarted;

    return { isStarted, isAborted: abortConnectionRef.current };
  };

  const waitGamecastStreamSession = (
    participantId: string,
    websocketSessionId: string,
    timeout: number,
    streamId: string,
    initMessageHandler: () => Promise<IInitMessage | null>,
    region: string,
    launchArgs: string[] | null,
  ): Promise<IGamecastStreamSession | null> => {
    return new Promise<IGamecastStreamSession>(async (resolve, reject) => {
      let lastStatTime = 0;
      let lastStatBytesReceived = 0;
      let statSamples: {
        downstreamTransferSpeed: string;
        downstreamConnectionSpeed: number;
        secondsElapsed: number;
      }[] = [];

      const description = 'Gamecast streaming session';
      const gameName = streamId;
      const videoElement = document.getElementById('gamecastVideoTag') as any;

      const gamecast: IGameCast = getGameCastClient(videoElement, true);

      gamecastClientRef.current = gamecast;

      gamecastRTCStatsIntervalRef.current = setInterval(async () => {
        const stats = await gamecast.getVideoRTCStats();

        for (const id of stats.keys()) {
          const stat = stats.get(id);

          if (stat.type === 'inbound-rtp') {
            // webrtc stats derived speed
            const webrtcStatsSpeed = {
              transferSpeed: 0,
              connectionSpeed: 0,
              avgTransferSpeed: 0,
              avgConnectionSpeed: 0,
              minSamplesCollected: false,
            };
            if (lastStatTime) {
              const mbytesReceived = (
                (stat.bytesReceived - lastStatBytesReceived) /
                Math.pow(1024, 2)
              ).toFixed(2);
              const secondsElapsed = (Date.now() - lastStatTime) / 1000;
              const bitrate = (8 * +mbytesReceived) / secondsElapsed;

              // keep only last WEBRTC_STATS_MIN_SAMPLES stat samples to calculate average speed
              // approx last 15 mins (WEBRTC_STATS_MIN_SAMPLES * 2 seconds)
              if (statSamples.length > WEBRTC_STATS_MIN_SAMPLES) {
                statSamples.shift();
              }
              statSamples.push({
                downstreamTransferSpeed: mbytesReceived,
                downstreamConnectionSpeed: +mbytesReceived * 8,
                secondsElapsed,
              });
              const avgTransferSpeed =
                statSamples.reduce(
                  (acc, cur) => acc + +cur.downstreamTransferSpeed,
                  0,
                ) / statSamples.length;
              const avgBitrate =
                statSamples.reduce(
                  (acc, cur) =>
                    acc +
                    (8 * +cur.downstreamTransferSpeed) / cur.secondsElapsed,
                  0,
                ) / statSamples.length;
              if (enableDebug) {
                console.warn(
                  formatString(
                    `current transfer speed: %sMBs\n server connection speed: %sMBPs\n avg transfer speed: %sMBs over %s samples\n avg server connection speed %s MBPs`,
                    mbytesReceived,
                    bitrate.toFixed(2),
                    avgTransferSpeed.toFixed(2),
                    statSamples.length,
                    avgBitrate.toFixed(2),
                  ),
                  {
                    stat,
                    mbytesReceived,
                    secondsElapsed,
                    bytesrec: stat.bytesReceived,
                    lastStatBytesReceived,
                    bitrate,
                  },
                );
              }
              webrtcStatsSpeed.transferSpeed = +mbytesReceived;
              webrtcStatsSpeed.connectionSpeed = +bitrate.toFixed(2);
              webrtcStatsSpeed.avgTransferSpeed = +avgTransferSpeed.toFixed(2);
              webrtcStatsSpeed.avgConnectionSpeed = +avgBitrate.toFixed(2);
            }
            lastStatTime = stat.timestamp;
            lastStatBytesReceived = stat.bytesReceived;
            // flag to wait 120 seconds before showing speed test check toaster
            // stats are collected every 2 seconds, 60 samples * 2 seconds = 120s
            webrtcStatsSpeed.minSamplesCollected = statSamples.length > 60;

            gamecastStats.current = {
              ...stat,
              ...webrtcStatsSpeed,
            };
          }
        }
      }, 2000);

      RegisterTouchToMouse(videoElement as any);

      (async () => {
        const gamecastSignalData = await gamecast.generateSignalRequest();

        gameliftStreamsLogger.log(`Starting gamecast player session`);

        const startGamecastPlayerSession = async (resolve: any) => {
          try {
            const initMessage = await initMessageHandler();
            if (!initMessage) {
              return;
            }

            showInitMessage(JSON.stringify(initMessage));

            if (abortConnectionRef.current) {
              return resolve(null);
            }

            await GameCastService.startGameSession({
              description,
              gameName,
              participantId,
              signalRequest: gamecastSignalData,
              timeout,
              streamId,
              initMessage,
              region,
              websocketSessionId,
              launchArgs,
              sessionKey: initMessage.sessionKey,
            });
          } catch (e) {
            gameliftStreamsLogger.log(
              `Couldn't start gamelift streams session`,
            );
          }
        };
        startGamecastPlayerSession(resolve);
      })();
    });
  };

  const createGamecastStreamSession = useCallback(
    async (
      userId: string,
      websocketSessionId: string,
      timeout: number,
      streamId: string,
      initMessageHandler: () => Promise<IInitMessage | null>,
      region: string,
      sendMessageToUnreal: (message: any) => void,
      launchArgs: string[] | null,
    ): Promise<IGamecastStreamSession | null> => {
      const gamecastStreamSession = await waitGamecastStreamSession(
        userId as string,
        websocketSessionId as string,
        timeout,
        streamId,
        initMessageHandler,
        region,
        launchArgs,
      );

      if (!gamecastStreamSession) {
        return null;
      }

      gameliftStreamsLogger.log('Gamelift streams session', {
        arn: gamecastStreamSession.Arn,
        applicationArn: gamecastStreamSession.ApplicationArn,
        userId: gamecastStreamSession.UserId,
        streamGroupId: gamecastStreamSession.StreamGroupId,
      });

      sendMessageToUnreal({
        action: 'CLIENT_CONNECTION_DATA',
        streamingProvider: 'GAMECAST',
        sessionArn: gamecastStreamSession.Arn,
        region,
      });

      return gamecastStreamSession;
    },
    [navigateToEventsPage],
  );

  const initializeGamecastStream = useCallback(
    async (
      participantId = '',
      websocketSessionId: string,
      initMessageHandler: () => Promise<IInitMessage | null>,
      region: string,
      launchArgs: string[] | null,
    ) => {
      let isStarted = false;
      if (!streamId) {
        return { isStarted };
      }

      try {
        const gamecastStreamSession = await createGamecastStreamSession(
          participantId,
          websocketSessionId,
          GAME_CAST_REQUEST_TIMEOUT,
          streamId,
          initMessageHandler,
          region,
          sendMessageToUnreal,
          launchArgs,
        );

        if (gamecastStreamSession) {
          sessionContextRef.current = {
            sessionArn: gamecastStreamSession.Arn,
            userId: gamecastStreamSession.UserId,
          };
        }
      } catch (e) {
        gameliftStreamsLogger.error(e);
      }

      return {
        isAborted: abortConnectionRef.current,
        gamecastRTCStatsInterval: gamecastRTCStatsIntervalRef.current,
      };
    },
    [streamId, createGamecastStreamSession, sendMessageToUnreal],
  );

  const handleReconnectGamecast = useCallback(
    async ({
      participantId,
      websocketSessionId,
      initMessageHandler,
      region,
      launchArgs,
      sessionKey,
    }: {
      participantId: string;
      websocketSessionId: string;
      initMessageHandler: () => Promise<IInitMessage | null>;
      region: string;
      launchArgs: string[] | null;
      sessionKey?: string;
    }) => {
      if (!participantId || !streamId) {
        return;
      }

      initializeGamecastConnectionTimeout();

      const signalResponse = await createGamecastStreamSession(
        participantId,
        websocketSessionId,
        GAME_CAST_REQUEST_TIMEOUT,
        streamId,
        initMessageHandler,
        region,
        sendMessageToUnreal,
        launchArgs,
      );

      if (signalResponse && gamecastClientRef.current) {
        clearGamecastConnectionTimeout();

        await gamecastClientRef.current.processSignalResponse(
          signalResponse.SignalResponse,
        );

        gamecastClientRef.current.attachInput();
      }
    },
    [streamId, initializeGamecastConnectionTimeout],
  );

  useEffect(() => {
    const handleBlur = () => {
      if (!currentRoom || !gamecastClientRef.current) {
        return;
      }

      if (!isDesktop && externalKeyboardConnected) {
        setShowExternalKeyboardOverlay(true);
      }

      try {
        gamecastClientRef.current?.detachInput();
      } catch (e) {
        console.error(e);
      }
    };

    const handleFocus = () => {
      if (!currentRoom || !gamecastClientRef.current) {
        return;
      }

      try {
        gamecastClientRef.current?.attachInput();
      } catch (e) {
        console.error(e);
      }
    };

    if (gamecastClientRef.current) {
      window.addEventListener('blur', handleBlur);
      window.addEventListener('focus', handleFocus);
    }

    return () => {
      if (gamecastClientRef.current) {
        window.removeEventListener('blur', handleBlur);
        window.removeEventListener('focus', handleFocus);
      }
    };
  }, [gamecastClientRef.current, currentRoom]);

  return {
    initializeGamecastStream,
    closeGamecastStream,
    abortGamecastConnection,
    handleReconnectGamecast,
    gamecastClientRef,
    processActiveStreamSession,
    initializeGamecastConnectionTimeout,
  };
};
